import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { 
  X, User, Building2, MapPin, CheckCircle2, XCircle, 
  TrendingUp, Award, Calendar, Eye
} from 'lucide-react';

const MetricBar = ({ label, value, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
    <Progress 
      value={value} 
      className={`h-1.5 ${color}`}
    />
  </div>
);

const CandidateColumn = ({ candidate, mandatorySkills, rank, onViewResume }) => {
  const metrics = candidate.metrics || {};
  
  return (
    <div className="min-w-[280px] max-w-[320px] flex-shrink-0 border rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-muted/30 border-b">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant={rank === 1 ? 'default' : 'secondary'} className="text-xs">
            #{rank}
          </Badge>
          <Badge 
            className={`text-xs ${
              candidate.fit_label === 'Strong Fit' 
                ? 'bg-green-500/10 text-green-500 border-green-500/20'
                : candidate.fit_label === 'Good Fit'
                ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                : candidate.fit_label === 'Risky Fit'
                ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                : 'bg-red-500/10 text-red-500 border-red-500/20'
            }`}
          >
            {candidate.fit_label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-lg truncate flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {candidate.name}
          </h3>
          {/* Eye icon to view resume */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => onViewResume?.(candidate)}
            data-testid={`compare-view-resume-${candidate.id}`}
          >
            <Eye className="h-4 w-4 text-muted-foreground hover:text-primary" strokeWidth={1.5} />
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          {candidate.experience_years && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {candidate.experience_years} yrs
            </span>
          )}
          {candidate.current_company && (
            <span className="flex items-center gap-1 truncate">
              <Building2 className="h-3 w-3" /> {candidate.current_company}
            </span>
          )}
        </div>
        {candidate.location && (
          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" /> {candidate.location}
          </div>
        )}
      </div>
      
      {/* Score */}
      <div className="p-4 border-b bg-primary/5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Overall Score</span>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-primary">
              {Math.round(candidate.final_score || 0)}
            </span>
            <span className="text-sm text-muted-foreground">/100</span>
          </div>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Skill Coverage</span>
            <span className="font-medium">{candidate.skill_coverage}%</span>
          </div>
          <Progress value={candidate.skill_coverage} className="h-2" />
        </div>
      </div>
      
      {/* Metrics */}
      <div className="p-4 border-b space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Performance Metrics
        </h4>
        <MetricBar label="Skill Fit" value={metrics.skill_fit || 0} color="[&>div]:bg-blue-500" />
        <MetricBar label="Cultural Fit" value={metrics.cultural_fit || 0} color="[&>div]:bg-purple-500" />
        <MetricBar label="Growth Potential" value={metrics.growth_potential || 0} color="[&>div]:bg-green-500" />
        <MetricBar label="Stability" value={metrics.stability || 0} color="[&>div]:bg-orange-500" />
      </div>
      
      {/* Readiness & Recommendation */}
      <div className="p-4 border-b flex gap-2">
        <div className="flex-1">
          <span className="text-xs text-muted-foreground">Readiness</span>
          <p className="text-sm font-medium">{candidate.role_readiness}</p>
        </div>
        <div className="flex-1">
          <span className="text-xs text-muted-foreground">Recommendation</span>
          <Badge 
            variant={candidate.recommendation === 'Shortlist' ? 'default' : 'secondary'}
            className="mt-0.5 text-xs"
          >
            {candidate.recommendation}
          </Badge>
        </div>
      </div>
      
      {/* Matched Skills */}
      <div className="p-4 border-b">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Matched Skills ({candidate.matched_skills?.length || 0}/{mandatorySkills?.length || 0})
        </h4>
        <div className="flex flex-wrap gap-1">
          {mandatorySkills?.map((skill, idx) => {
            const isMatched = candidate.matched_skills?.some(
              ms => ms.toLowerCase() === skill.toLowerCase()
            );
            return (
              <Badge 
                key={idx}
                variant="outline"
                className={`text-xs ${
                  isMatched 
                    ? 'bg-green-500/10 text-green-500 border-green-500/30' 
                    : 'bg-red-500/10 text-red-500 border-red-500/30 line-through opacity-60'
                }`}
              >
                {isMatched && <CheckCircle2 className="h-3 w-3 mr-1" />}
                {skill}
              </Badge>
            );
          })}
        </div>
      </div>
      
      {/* Flags */}
      <div className="p-4 space-y-3">
        {candidate.green_flags?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-green-500 mb-1.5 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Green Flags
            </h4>
            <div className="space-y-1">
              {candidate.green_flags.slice(0, 3).map((flag, idx) => (
                <p key={idx} className="text-xs text-muted-foreground">• {flag}</p>
              ))}
            </div>
          </div>
        )}
        
        {candidate.red_flags?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-red-500 mb-1.5 flex items-center gap-1">
              <XCircle className="h-3 w-3" /> Red Flags
            </h4>
            <div className="space-y-1">
              {candidate.red_flags.slice(0, 3).map((flag, idx) => (
                <p key={idx} className="text-xs text-muted-foreground">• {flag}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const QuickCompareModal = ({ 
  isOpen,
  onClose,
  comparisonData, 
  isLoading,
  onViewResume
}) => {
  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <Skeleton className="h-6 w-48" />
          </DialogHeader>
          <div className="p-4 flex gap-4 overflow-x-auto">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-[500px] w-[280px] shrink-0" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!comparisonData || !comparisonData.candidates || comparisonData.candidates.length < 2) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] p-0" data-testid="quick-compare-modal">
        <DialogHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Award className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Quick Compare - {comparisonData.job_title}
            </DialogTitle>
            <Badge variant="secondary" className="text-xs">
              {comparisonData.candidates.length} Candidates
            </Badge>
          </div>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-80px)]">
          <div className="p-4">
            <div className="flex gap-4 overflow-x-auto pb-4" style={{ minWidth: 'max-content' }}>
              {comparisonData.candidates.map((candidate, idx) => (
                <CandidateColumn 
                  key={candidate.id}
                  candidate={candidate}
                  mandatorySkills={comparisonData.mandatory_skills}
                  rank={idx + 1}
                  onViewResume={onViewResume}
                />
              ))}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default QuickCompareModal;
